import React, { memo, useCallback, useMemo, useState } from 'react';
import { API_BASE } from "../../apiBase";
import { useAuth0 } from "@auth0/auth0-react";
import './CancellationPage.css';

const CANCELLATION_ENDPOINT = API_BASE + "/cancellation-feedback";


const HIGHLIGHTED_PHRASES = [
  '20% off your recurring payments',
  'Schedule a free 1-on-1 session with a Nepeto expert',
  'the right guidance',
  'Book 4 free 1-on-1 sessions with a Nepeto expert',
  'Book a free live video call with our technical team'
];

const OPTIONS = [
  {
    id: 'too_expensive',
    label: 'Too expensive',
    defaultText: 'We get it — budgets matter. 💸\nWe’d hate to see you go, so here’s 20% off your recurring payments from now on to make things easier. \n\nKeep enjoying automated sourcing for Amazon — at a price that fits you better.',
    defaultBtnText: 'Claim 20% off',
    defaultBtnUrl: ''
  },
  {
    id: 'dont_use_enough',
    label: "I don't use it enough",
    defaultText: 'You might be missing the best part of Nepeto 💡\nMany users discover Nepeto’s biggest time-saving features only after a quick walkthrough.\n\nSchedule a free 1-on-1 session with a Nepeto expert — they’ll personally show you how to save hours each week and get more done effortlessly.',
    defaultBtnText: 'Book My Free 1-on-1 Session',
    defaultBtnUrl: 'https://calendly.com/igprojects/why-did-you-want-to-cancel'
  },
  {
    id: 'no_profitable_products',
    label: 'I did not find profitable products',
    defaultText: 'Finding profitable products can feel tough — until you know the shortcuts 🚀\nNepeto actually does the heavy lifting for you — it’s a powerful automated leads generator built to uncover high-potential products.\n\nYou just need the right guidance to tap into it.\n\nBook 4 free 1-on-1 sessions with a Nepeto expert who’ll show you step by step how to find tons of profitable products and make Nepeto work for you.',
    defaultBtnText: 'Book My 4 Free Calls',
    defaultBtnUrl: 'https://calendly.com/igprojects/let-s-maximize-your-profits'
  },
  {
    id: 'technical_issues',
    label: 'Technical issues',
    defaultText: 'Let’s fix this - fast ⚡\nWe’re sorry to hear you ran into technical issues. The good news? Our experts can solve most problems in just a few minutes.\n\nBook a free live video call with our technical team, and we’ll get everything running smoothly right away - so you can get back to using Nepeto without frustration.',
    defaultBtnText: 'Start My Live Support Call',
    defaultBtnUrl: 'https://calendly.com/igprojects/nepeto-technical-support?back=1&month=2025-10'
  },
  { id: 'other', label: 'Other', defaultText: 'Tell us more and we will offer a tailored solution.', defaultBtnText: '', defaultBtnUrl: '' }
];

const DEFAULT_SURVEY = { choice: '', open1: '', rating: null, open2: '', confirmed: false };

const MODES = { OFFER: 'offer', CANCELLED: 'cancelled', STAY: 'stay' };

const DEFAULT_MODAL = { open: false, text: '', btnText: '', btnUrl: '', optionId: null, otherInput: '', mode: MODES.OFFER };

const OptionButton = memo(({ children, selected, onClick }) => (
  <button className={`cp-option ${selected ? 'selected' : ''}`} onClick={onClick} type="button">
    {children}
  </button>
));

const formatHighlight = (text) => {
  if (!text) return null;
  const nodes = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    let earliestIndex = -1;
    let foundPhrase = null;

    for (const phrase of HIGHLIGHTED_PHRASES) {
      const idx = remaining.indexOf(phrase);
      if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) {
        earliestIndex = idx;
        foundPhrase = phrase;
      }
    }

    if (earliestIndex === -1) {
      nodes.push(<span key={key++}>{remaining}</span>);
      break;
    }

    if (earliestIndex > 0) {
      nodes.push(<span key={key++}>{remaining.slice(0, earliestIndex)}</span>);
    }

    nodes.push(<strong key={key++}>{foundPhrase}</strong>);
    remaining = remaining.slice(earliestIndex + foundPhrase.length);
  }

  return nodes;
};

export default function CancellationPage() {
  const savedMessages = useMemo(() => {
    try {
      const raw = localStorage.getItem('cp_custom_messages');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const {
    isAuthenticated,
    getAccessTokenSilently,
    user
  } = useAuth0();

  const [survey, setSurvey] = useState(DEFAULT_SURVEY);
  const [modal, setModal] = useState(DEFAULT_MODAL);
  const [confirmModal, setConfirmModal] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [requestError, setRequestError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modalTitle, modalBody] = useMemo(() => {
    if (!modal.text) return ['', ''];
    const [title, ...rest] = modal.text.split('\n');
    return [title, rest.join('\n')];
  }, [modal.text]);

  const handleSurveyChange = useCallback((changes) => {
    setSurvey((prev) => ({ ...prev, ...changes }));
  }, []);

  const getSavedMessage = useCallback(
    (option) => {
      const key = `0::${option.id}`;
      return (
        savedMessages[key] ||
        savedMessages[`0::${option.label}`] ||
        savedMessages[option.label] ||
        savedMessages[option.id] ||
        {}
      );
    },
    [savedMessages]
  );

  const openOptionModal = useCallback(
    (option) => {
      const saved = getSavedMessage(option);
      const text =
        option.defaultText ||
        (typeof saved === 'object' && saved.text) ||
        (typeof saved === 'string' ? saved : '') ||
        'Thank you for your feedback.';
      const btnText =
        option.defaultBtnText !== undefined && option.defaultBtnText !== null && option.defaultBtnText !== ''
          ? option.defaultBtnText
          : saved.btnText || '';
      const btnUrl =
        option.defaultBtnUrl !== undefined && option.defaultBtnUrl !== null && option.defaultBtnUrl !== ''
          ? option.defaultBtnUrl
          : saved.btnUrl || '';

      setModal({
        open: true,
        text,
        btnText,
        btnUrl,
        optionId: option.id,
        otherInput: saved.otherInput || '',
        mode: MODES.OFFER
      });
    },
    [getSavedMessage]
  );

  const closeModal = useCallback(() => {
    setModal({ ...DEFAULT_MODAL });
  }, []);

  const handleStay = useCallback(() => {
    setModal({
      open: true,
      text: 'Thanks for staying!\nWe appreciate you staying with Nepeto. Click OK to go back to Nepeto.',
      btnText: '',
      btnUrl: '',
      optionId: null,
      otherInput: '',
      mode: MODES.STAY
    });
  }, []);

  const handleCancelAttempt = useCallback(() => {
    if (!survey.confirmed) {
      setSubmitError('Please confirm that you want to cancel your Nepeto subscription before proceeding.');
      return;
    }
    setSubmitError('');
    setRequestError(false);
    setConfirmModal(true);
  }, [survey.confirmed]);

  const handleSendOther = useCallback(() => {
    setSurvey((prev) => ({ ...prev, open2: modal.otherInput }));
    closeModal();
  }, [closeModal, modal.otherInput]);

  const submitCancellation = useCallback(async () => {
    if (isSubmitting) return;
    setRequestError(false);
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(CANCELLATION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          reason: survey.choice,
          followUp: survey.open1,
          likelihoodToReturn: survey.rating,
          additionalComments: survey.open2,
          otherReason: modal.optionId === 'other' ? modal.otherInput : '',
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.detail || payload?.message || 'Unable to process your request right now. Please try again.';
        throw new Error(message);
      }

      setConfirmModal(false);
      try {
        localStorage.setItem('cp_last_cancel_feedback', JSON.stringify(survey));
      } catch { }
      setModal({
        open: true,
        text: 'Your subscription has been cancelled. Thank you for trying Nepeto.',
        btnText: '',
        btnUrl: '',
        optionId: null,
        otherInput: '',
        mode: MODES.CANCELLED
      });
    } catch (error) {
      setRequestError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, modal.optionId, modal.otherInput, survey]);

  const handleBackdropClick = useCallback(() => {
    if (modal.mode === MODES.OFFER) {
      closeModal();
    }
  }, [closeModal, modal.mode]);

  return (
    <div className="cp-page">
      <div className="cp-card">
        <div className="cp-header">
          <img src="/logoNew.png" alt="Nepeto" className="cp-logo" />
          <hr className="cp-sep" />
        </div>

        <h2 className="cp-title">We're sorry to see you go</h2>
        <p className="cp-sub">Please take a moment to answer the following.</p>

        <div className="cp-grid-single">
          <div className="cp-survey">
            <h3 className="cp-survey-title">1. Please share the reason you are leaving</h3>
            <div className="cp-options">
              {OPTIONS.map((opt) => (
                <OptionButton
                  key={opt.id}
                  selected={survey.choice === opt.id}
                  onClick={() => {
                    handleSurveyChange({ choice: opt.id });
                    openOptionModal(opt);
                  }}
                >
                  {opt.label}
                </OptionButton>
              ))}
            </div>

            <h3 className="cp-survey-title">2. What will you do now?</h3>
            <textarea className="cp-textarea" value={survey.open1} onChange={(e) => handleSurveyChange({ open1: e.target.value })} />

            <h3 className="cp-survey-title">3. How likely are you to return from 1-10?</h3>
            <div className="cp-rating">
              {[...Array(10)].map((_, i) => {
                const val = i + 1;
                return (
                  <label key={val} className={`cp-rating-item ${survey.rating === val ? 'active' : ''}`}>
                    <input type="radio" name="rating-0" value={val} checked={survey.rating === val} onChange={() => handleSurveyChange({ rating: val })} />
                    {val}
                  </label>
                );
              })}
            </div>

            <h3 className="cp-survey-title">4. Anything else you want to add?</h3>
            <textarea className="cp-textarea" value={survey.open2} onChange={(e) => handleSurveyChange({ open2: e.target.value })} />

            <div className="cp-confirm">
              <input id="confirm-0" type="checkbox" checked={survey.confirmed} onChange={(e) => handleSurveyChange({ confirmed: e.target.checked })} />
              <label htmlFor="confirm-0">
                <strong>*I confirm that I want to cancel my subscription</strong>
              </label>
            </div>
          </div>
        </div>

        {submitError && <div className="cp-error">{submitError}</div>}

        <div className="cp-actions">
          <button className="cp-stay" onClick={handleStay}>Stay</button>
          <button className="cp-cancel" onClick={handleCancelAttempt}>Cancel</button>
        </div>
      </div>

      {modal.open && (
        <div className="cp-modal-backdrop" onClick={handleBackdropClick}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="cp-modal-body">
              <div>
                <div style={{ marginTop: '0.5rem' }}>
                  {modalTitle ? <div className="cp-modal-title"><strong>{modalTitle}</strong></div> : null}
                  {modalBody ? <div className="cp-modal-preview">{formatHighlight(modalBody)}</div> : null}
                  {modal.optionId === 'other' && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <label className="cp-label">Please tell us more about your reason:</label>
                      <textarea className="cp-textarea" value={modal.otherInput} onChange={(e) => setModal((prev) => ({ ...prev, otherInput: e.target.value }))} placeholder="Write your reason here..." />
                    </div>
                  )}
                  <div style={{ marginTop: '0.8rem' }} />
                </div>
              </div>
            </div>
            <div className="cp-modal-actions cp-modal-actions-centered">
              {modal.mode === MODES.CANCELLED ? (
                <button className="cp-primary" onClick={() => { window.location.href = 'https://www.nepeto.com/pricing'; }}>Close page</button>
              ) : modal.mode === MODES.STAY ? (
                <button className="cp-primary" onClick={() => { window.location.href = 'https://www.nepeto.com/'; }}>OK</button>
              ) : modal.optionId === 'other' ? (
                <button className="cp-primary" onClick={handleSendOther}>Send</button>
              ) : (
                <>
                  {modal.btnText ? (
                    modal.btnUrl ? (
                      <a className="cp-cta" href={modal.btnUrl} target="_blank" rel="noreferrer">{modal.btnText}</a>
                    ) : (
                      <button className="cp-cta disabled" disabled>{modal.btnText}</button>
                    )
                  ) : null}
                  <button className="cp-secondary cp-no" onClick={closeModal}>Proceed to Cancel</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="cp-modal-backdrop" onClick={() => { if (!isSubmitting) setConfirmModal(false); }}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="cp-modal-body">
              <h3>Confirm cancellation</h3>
              <p>Are you sure you want to cancel? This will submit the feedback you provided.</p>
              {requestError && <div className="cp-error">Something went wrong. If the issue presists, contact us at <a href="mailto:support@nepeto.com">support@nepeto.com</a></div>}
            </div>
            <div className="cp-modal-actions">
              <button className="cp-secondary" onClick={() => setConfirmModal(false)} disabled={isSubmitting}>Back</button>
              <button className={`cp-primary ${isSubmitting ? 'is-loading' : ''}`} onClick={submitCancellation} disabled={isSubmitting}>
                {isSubmitting && <span className="cp-spinner" aria-hidden="true" />}
                {isSubmitting ? 'Submitting...' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
